import React, { useState, useEffect } from 'react';
import { X, FolderOpen } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { format } from 'date-fns';
import { getLocalDateKey } from '../utils/dateFormatter';

const LogFormModal = ({ log, selectedDate, currentUserId, userDriveLink, readOnly = false, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    date: selectedDate,
    todays_focus: '',
    work_done: '',
    blockers: '',
    next_steps: ''
  });
  const [loading, setLoading] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);

  useEffect(() => {
    if (log) {
      // Editing existing log or viewing log
      setFormData({
        date: log.log_date || selectedDate,
        todays_focus: log.focus || '',
        work_done: log.work_done || '',
        blockers: log.blockers || '',
        next_steps: log.next_steps || ''
      });
    } else if (!readOnly) {
      // Creating new log - check if we should auto-fill (only if not read-only)
      const today = getLocalDateKey(new Date());
      if (selectedDate === today) {
        autoFillFromYesterday();
      } else {
        setFormData({
          date: selectedDate,
          todays_focus: '',
          work_done: '',
          blockers: '',
          next_steps: ''
        });
      }
    } else {
      // Read-only mode with no log - shouldn't happen, but handle gracefully
      setFormData({
        date: selectedDate,
        todays_focus: '',
        work_done: '',
        blockers: '',
        next_steps: ''
      });
    }
  }, [log, selectedDate, readOnly]);

  const autoFillFromYesterday = async () => {
    if (!currentUserId) return;

    try {
      setAutoFilling(true);
      
      // Calculate yesterday's date
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayDate = getLocalDateKey(yesterday);

      // Query yesterday's log for this user
      const { data, error } = await supabase
        .from('daily_logs')
        .select('*')
        .eq('user_id', currentUserId)
        .eq('log_date', yesterdayDate)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 is "no rows returned" - that's fine, just no log yesterday
        console.error('Error fetching yesterday log:', error);
      }

      // If we found yesterday's log and it has next_steps, auto-fill
      if (data && data.next_steps && data.next_steps.trim() !== '') {
        setFormData(prev => ({
          ...prev,
          todays_focus: data.next_steps.trim()
        }));
      } else {
        // If no log yesterday, try to find the most recent previous log
        const { data: recentLog, error: recentError } = await supabase
          .from('daily_logs')
          .select('*')
          .eq('user_id', currentUserId)
          .lt('log_date', yesterdayDate)
          .order('log_date', { ascending: false })
          .limit(1)
          .single();

        if (!recentError && recentLog && recentLog.next_steps && recentLog.next_steps.trim() !== '') {
          setFormData(prev => ({
            ...prev,
            todays_focus: recentLog.next_steps.trim()
          }));
        }
      }
    } catch (error) {
      console.error('Error in auto-fill:', error);
    } finally {
      setAutoFilling(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setLoading(true);

    try {
      await onSubmit({
        ...formData,
        date: selectedDate
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="log-form-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold" data-testid="form-modal-title">
              {readOnly ? 'View Daily Log' : (log ? 'Edit Log' : 'Create Daily Log')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="form-modal-close-btn"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 scrollbar-thin" style={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>
          {autoFilling && (
            <div className="p-3 bg-white/5 border border-white/20 rounded-lg text-sm text-gray-300 animate-pulse">
              Auto-filling from previous log...
            </div>
          )}

          {/* Date (read-only for editing, editable for new) */}
          <div>
            <label className="block text-sm font-medium mb-2">Date</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full glass-input"
              disabled={!!log || readOnly}
              data-testid="form-date-input"
            />
          </div>

          {/* Today's Focus */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Today's Focus {!readOnly && <span className="text-white font-bold">*</span>}
            </label>
            <textarea
              name="todays_focus"
              value={formData.todays_focus}
              onChange={handleChange}
              className="w-full glass-input resize-none"
              rows="3"
              placeholder="What are you focusing on today?"
              required={!readOnly}
              disabled={readOnly}
              readOnly={readOnly}
              data-testid="form-focus-input"
            />
          </div>

          {/* Drive Folder Link */}
          {userDriveLink && userDriveLink.trim() !== '' && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => window.open(userDriveLink, '_blank')}
                className="text-sm text-white hover:text-gray-300 flex items-center gap-2 underline transition-all font-medium"
                data-testid="form-drive-link"
              >
                <FolderOpen size={16} />
                Open Drive Folder
              </button>
            </div>
          )}

          {/* Work Done */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Work Done {!readOnly && <span className="text-white font-bold">*</span>}
            </label>
            <textarea
              name="work_done"
              value={formData.work_done}
              onChange={handleChange}
              className="w-full glass-input resize-none"
              rows="4"
              placeholder="What work have you completed?"
              required={!readOnly}
              disabled={readOnly}
              readOnly={readOnly}
              data-testid="form-work-done-input"
            />
          </div>

          {/* Blockers */}
          <div>
            <label className="block text-sm font-medium mb-2">Blockers</label>
            <textarea
              name="blockers"
              value={formData.blockers}
              onChange={handleChange}
              className="w-full glass-input resize-none"
              rows="3"
              placeholder="Any blockers or issues?"
              disabled={readOnly}
              readOnly={readOnly}
              data-testid="form-blockers-input"
            />
          </div>

          {/* Next Steps */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Next Steps {!readOnly && <span className="text-white font-bold">*</span>}
            </label>
            <textarea
              name="next_steps"
              value={formData.next_steps}
              onChange={handleChange}
              className="w-full glass-input resize-none"
              rows="3"
              placeholder="What are your next steps?"
              required={!readOnly}
              disabled={readOnly}
              readOnly={readOnly}
              data-testid="form-next-steps-input"
            />
          </div>
        </form>

        {/* Actions */}
        <div className="p-6 border-t border-white/10 flex gap-3 justify-end">
          {readOnly ? (
            <button
              onClick={onClose}
              className="px-4 py-2 rounded text-white font-medium transition-colors bg-gray-600 hover:bg-gray-500"
              data-testid="form-close-btn"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="btn-secondary"
                data-testid="form-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`px-6 py-2 rounded-lg font-bold transition-all ${
                  loading
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                    : 'bg-white text-black hover:bg-gray-200 shadow-[0_0_15px_rgba(255,255,255,0.1)]'
                }`}
                data-testid="form-save-btn"
              >
                {loading ? 'Saving...' : (log ? 'Update' : 'Create')}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default LogFormModal;

